import { useCallback, useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { getThemeColor } from '@/shared/hooks/useThemeColors'
import { DbShell } from './DbShell'
import { MasterEye } from './MasterEye'
import { curvedPathToCenter, nodeSeed } from './svgMath'
import { useScrollReveal } from './useScrollReveal'

/* ──────────────────────────────────────────────────────────
 * Intelligence Map — Asymmetric, high-density data flow.
 * Hand-placed node clusters connected to a central DB Eye.
 * No concentric circles. Organic, kinetic layout.
 * ────────────────────────────────────────────────────────── */

const CX = 450
const CY = 360

/* ── Icons ── */

const ICONS = {
  facebook: {
    d: 'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z',
    fill: true,
  },
  x: {
    d: 'M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932 6.064-6.932zm-1.292 19.49h2.039L6.486 3.24H4.298l13.311 17.403z',
    fill: true,
  },
  threads: {
    d: 'M10 13a3.5 3.5 0 0 0 4 0 M12 11.5a3.5 3.5 0 1 0 3.5 3.5 M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z',
    fill: false,
  },
  news: {
    d: 'M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2zm0 0a2 2 0 0 1-2-2v-9c0-1.1.9-2 2-2h2M9 6h7M9 10h7M9 14h3',
    fill: false,
  },
  telegram: { d: 'M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z', fill: false },
  linkedin: {
    d: 'M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-4 0v7h-4v-7a6 6 0 0 1 6-6zM2 9h4v12H2zM4 2a2 2 0 1 1 0 4 2 2 0 0 1 0-4z',
    fill: false,
  },
  rss: {
    d: 'M4 11a9 9 0 0 1 9 9M4 4a16 16 0 0 1 16 16M5 20a1 1 0 1 0 0-2 1 1 0 0 0 0 2z',
    fill: false,
  },
  monitor: {
    d: 'M12 20h-1a7 7 0 0 1-7-7V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v8a7 7 0 0 1-7 7h-1M7.5 12H10M14 12h2.5',
    fill: false,
  },
} as const

type IconKey = keyof typeof ICONS

/* ── Asymmetric node placement ── */

interface NodeDef {
  icon: IconKey
  x: number
  y: number
  r: number // node circle radius
  iconScale: number
  drift: 'a' | 'b' | 'c' // CSS drift variant
}

// Hand-placed nodes in 4 clusters + scattered fill
const NODES: NodeDef[] = [
  // ── Cluster A: top-left — news-heavy, tight ──
  { icon: 'news', x: 115, y: 140, r: 24, iconScale: 0.75, drift: 'a' },
  { icon: 'rss', x: 170, y: 100, r: 18, iconScale: 0.58, drift: 'b' },
  { icon: 'news', x: 80, y: 210, r: 16, iconScale: 0.5, drift: 'c' },

  // ── Cluster B: top-right — social mix, medium spacing ──
  { icon: 'x', x: 680, y: 120, r: 22, iconScale: 0.7, drift: 'b' },
  { icon: 'telegram', x: 750, y: 180, r: 18, iconScale: 0.58, drift: 'a' },
  { icon: 'facebook', x: 630, y: 190, r: 20, iconScale: 0.65, drift: 'c' },
  { icon: 'threads', x: 720, y: 260, r: 15, iconScale: 0.48, drift: 'b' },

  // ── Cluster C: bottom-left — monitors, loose ──
  { icon: 'monitor', x: 130, y: 480, r: 20, iconScale: 0.65, drift: 'c' },
  { icon: 'rss', x: 200, y: 540, r: 16, iconScale: 0.5, drift: 'a' },
  { icon: 'linkedin', x: 80, y: 560, r: 14, iconScale: 0.45, drift: 'b' },

  // ── Cluster D: bottom-right — social duplicates, tight ──
  { icon: 'facebook', x: 720, y: 500, r: 22, iconScale: 0.7, drift: 'a' },
  { icon: 'x', x: 780, y: 440, r: 18, iconScale: 0.58, drift: 'c' },
  { icon: 'telegram', x: 670, y: 560, r: 15, iconScale: 0.48, drift: 'b' },

  // ── Scattered fill — between clusters ──
  { icon: 'threads', x: 250, y: 320, r: 16, iconScale: 0.5, drift: 'a' },
  { icon: 'linkedin', x: 640, y: 380, r: 16, iconScale: 0.5, drift: 'c' },
  { icon: 'monitor', x: 380, y: 580, r: 14, iconScale: 0.45, drift: 'b' },
]

/* Floating labels near clusters */
interface LabelDef {
  text: string
  x: number
  y: number
}
const LABELS: LabelDef[] = [
  { text: 'MNGIAN NEWS', x: 108, y: 80 },
  { text: 'Real-time feed', x: 665, y: 90 },
  { text: 'Sentiment sync', x: 115, y: 440 },
  { text: 'Post aggregation', x: 700, y: 400 },
]

/* Pre-computed connection paths (deterministic, never change) */
const DESKTOP_PATHS = NODES.map((n) => curvedPathToCenter(n.x, n.y, CX, CY))

/* ── Source Dock (right sidebar) ── */

interface DockSource {
  key: 'facebook' | 'x' | 'threads' | 'news'
  icon: IconKey
}

const DOCK_SOURCES: DockSource[] = [
  { key: 'facebook', icon: 'facebook' },
  { key: 'x', icon: 'x' },
  { key: 'threads', icon: 'threads' },
  { key: 'news', icon: 'news' },
]

function SourceDock() {
  const { t } = useTranslation()
  const iconColor = getThemeColor('--chart-label')

  return (
    <>
      {/* Desktop: fixed right dock */}
      <div
        className={cn(
          'absolute top-1/2 -translate-y-1/2 hidden lg:flex flex-col items-stretch',
          'right-2 xl:right-6',
          'rounded-xl py-4 px-2.5',
          'border-l border-[var(--glass-ring)] bg-[var(--glass-surface)] backdrop-blur-sm',
        )}
      >
        {/* Header */}
        <div className="mb-4 text-center text-[8px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
          {t('landing.intelligenceMap.dock.title')}
        </div>

        {/* Icon list */}
        <div className="flex flex-col items-center gap-3">
          {DOCK_SOURCES.map((source) => {
            const icon = ICONS[source.icon]
            return (
              <div key={source.key} className="group relative">
                {/* Tooltip — appears left of icon on hover */}
                <div
                  className={cn(
                    'pointer-events-none absolute right-full top-1/2 mr-3 -translate-y-1/2',
                    'whitespace-nowrap rounded-md px-2.5 py-1.5 text-[10px] font-medium tracking-wide',
                    'opacity-0 transition-all duration-200 group-hover:opacity-100 group-hover:translate-x-0 translate-x-1',
                    'bg-card/90 text-muted-foreground ring-1 ring-border',
                  )}
                >
                  {t(`landing.intelligenceMap.dock.tooltips.${source.key}`)}
                </div>

                {/* Icon frame */}
                <div className="relative">
                  <div
                    className={cn(
                      'flex h-9 w-9 items-center justify-center rounded-lg transition-all duration-200',
                      'group-hover:scale-110',
                      'bg-[var(--glass-surface)] ring-1 ring-[var(--glass-ring)]',
                      'group-hover:bg-[var(--glass-surface-hover)] group-hover:ring-[var(--glass-ring)]',
                    )}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      width={14}
                      height={14}
                      className="transition-colors duration-200 text-muted-foreground group-hover:text-primary"
                    >
                      {icon.fill ? (
                        <path d={icon.d} fill="currentColor" />
                      ) : (
                        <path
                          d={icon.d}
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      )}
                    </svg>
                  </div>

                  {/* Active indicator dot */}
                  <div className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-primary shadow-[0_0_6px_var(--chart-glow-strong)]" />
                </div>
              </div>
            )
          })}
        </div>

        {/* Bottom divider + status dot */}
        <div className="mt-4 flex flex-col items-center gap-3">
          <div className="h-6 w-px bg-[var(--glass-ring)]" />
          <div className="h-1.5 w-1.5 rounded-full bg-[var(--sentiment-positive)]/70" />
        </div>
      </div>

      {/* Mobile: horizontal dock at bottom */}
      <div
        className={cn(
          'absolute inset-x-0 bottom-0 flex items-center justify-center gap-3 py-3 lg:hidden',
          'border-t border-[var(--glass-ring)] bg-[var(--glass-surface)] backdrop-blur-sm',
        )}
      >
        <span className="text-[8px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
          {t('landing.intelligenceMap.dock.title')}
        </span>
        {DOCK_SOURCES.map((source) => {
          const icon = ICONS[source.icon]
          return (
            <div key={source.key} className="relative">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[var(--glass-surface)] ring-1 ring-[var(--glass-ring)]">
                <svg viewBox="0 0 24 24" width={12} height={12}>
                  {icon.fill ? (
                    <path d={icon.d} fill={iconColor} />
                  ) : (
                    <path
                      d={icon.d}
                      fill="none"
                      stroke={iconColor}
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  )}
                </svg>
              </div>
              <div className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full bg-primary" />
            </div>
          )
        })}
      </div>
    </>
  )
}

/* ── Desktop SVG ── */

function DesktopField({ id, pingCount }: { id: string; pingCount: number }) {
  const lineColor = getThemeColor('--chart-line')
  const glowColor = getThemeColor('--chart-glow')
  const nodeStroke = getThemeColor('--chart-node-stroke')
  const nodeFill = getThemeColor('--chart-node-fill')
  const iconColor = getThemeColor('--chart-label')
  const packetColor = getThemeColor('--chart-particle')
  const labelColor = getThemeColor('--text-3')

  return (
    <svg
      viewBox="0 0 900 720"
      className="absolute inset-0 h-full w-full"
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        <filter id={`${id}-gl`} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="2" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <filter id={`${id}-pk`} x="-100%" y="-100%" width="300%" height="300%">
          <feDropShadow
            dx="0"
            dy="0"
            stdDeviation="3"
            floodColor={packetColor}
            floodOpacity="0.6"
          />
        </filter>
        <filter id={`${id}-pg`} x="-200%" y="-200%" width="500%" height="500%">
          <feDropShadow
            dx="0"
            dy="0"
            stdDeviation="6"
            floodColor={lineColor}
            floodOpacity="0.4"
          />
        </filter>
      </defs>

      {/* Radar ripples */}
      {[0, 2.5, 5, 7.5, 10].map((d, i) => (
        <circle
          key={`r-${i}`}
          cx={CX}
          cy={CY}
          r="30"
          fill="none"
          stroke={lineColor}
          strokeWidth="0.5"
          className="intel-radar-expand"
          style={{ '--radar-expand-delay': `${d}s` } as React.CSSProperties}
        />
      ))}

      {/* DB disks behind center */}

      {/* Floating cluster labels */}
      {LABELS.map((l, i) => (
        <text
          key={i}
          x={l.x}
          y={l.y}
          fontSize="8"
          fontFamily="var(--font-sans)"
          fill={labelColor}
          letterSpacing="0.15em"
          className="uppercase"
        >
          {l.text}
        </text>
      ))}

      {/* Connections + particles + nodes */}
      {NODES.map((node, i) => {
        const path = DESKTOP_PATHS[i]
        const icon = ICONS[node.icon]
        const s = nodeSeed(i)
        const dur = 5 + s * 3
        const baseDelay = s * 7
        const iconOff = 12 * node.iconScale
        const sd1 = (s * 3).toFixed(1)
        const sd2 = (s * 3 + 1.1).toFixed(1)

        return (
          <g key={i}>
            {/* Glow */}
            <path
              d={path}
              fill="none"
              stroke={glowColor}
              strokeWidth="2.5"
              filter={`url(#${id}-gl)`}
              className="intel-connection"
              style={
                { '--intel-delay': `${(i * 0.1).toFixed(2)}s` } as React.CSSProperties
              }
            />
            {/* Line */}
            <path
              d={path}
              fill="none"
              stroke={lineColor}
              strokeWidth="0.8"
              strokeOpacity="0.10"
              strokeLinecap="round"
              className="intel-connection"
              style={
                { '--intel-delay': `${(i * 0.1).toFixed(2)}s` } as React.CSSProperties
              }
            />
            {/* Scan pulses */}
            {[sd1, sd2].map((sd, si) => (
              <path
                key={si}
                d={path}
                fill="none"
                stroke={lineColor}
                strokeWidth="1"
                strokeLinecap="round"
                pathLength={1}
                className="intel-scan-pulse"
                style={
                  {
                    '--scan-dur': '2.2s',
                    '--scan-delay': `${sd}s`,
                  } as React.CSSProperties
                }
              />
            ))}

            {/* Particle cluster (3 per node) */}
            {[0, 0.35, 0.7].map((off, pi) => (
              <circle key={pi} r={pi === 0 ? 2.5 : 1.2} fill={packetColor} opacity="0">
                <animateMotion
                  path={path}
                  dur={`${dur.toFixed(1)}s`}
                  begin={`${(baseDelay + off).toFixed(2)}s`}
                  repeatCount="indefinite"
                  calcMode="spline"
                  keySplines="0.4 0 0.2 1"
                  keyTimes="0;1"
                />
                <animate
                  attributeName="opacity"
                  values="0;0.7;0.7;0"
                  keyTimes="0;0.08;0.75;1"
                  dur={`${dur.toFixed(1)}s`}
                  begin={`${(baseDelay + off).toFixed(2)}s`}
                  repeatCount="indefinite"
                />
              </circle>
            ))}

            {/* Node with drift */}
            <g className={`intel-drift-${node.drift}`}>
              <g transform={`translate(${node.x},${node.y})`}>
                <circle
                  r={node.r}
                  fill={nodeFill}
                  stroke={nodeStroke}
                  strokeWidth="1"
                  className="intel-node"
                  style={
                    {
                      '--intel-delay': `${(i * 0.08).toFixed(2)}s`,
                    } as React.CSSProperties
                  }
                />
                <g
                  transform={`translate(${-iconOff},${-iconOff}) scale(${node.iconScale})`}
                >
                  {icon.fill ? (
                    <path d={icon.d} fill={iconColor} />
                  ) : (
                    <path
                      d={icon.d}
                      fill="none"
                      stroke={iconColor}
                      strokeWidth={node.r < 16 ? 2 : 1.5}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  )}
                </g>
              </g>
            </g>
          </g>
        )
      })}

      {/* Blink ping ripples */}
      {pingCount > 0 && (
        <g key={`ping-${pingCount}`}>
          {[0, 0.12, 0.24].map((d, i) => (
            <circle
              key={i}
              cx={CX}
              cy={CY}
              r="10"
              fill="none"
              stroke={i === 0 ? packetColor : lineColor}
              strokeWidth={i === 0 ? 1.8 : 0.8}
              filter={i === 0 ? `url(#${id}-pg)` : undefined}
              className="intel-blink-ping"
              style={{ '--ping-delay': `${d}s` } as React.CSSProperties}
            />
          ))}
        </g>
      )}
    </svg>
  )
}

/* ── Mobile (simplified: 8 nodes, hand-placed) ── */

const MCX = 200
const MCY = 260

const M_NODES: NodeDef[] = [
  { icon: 'facebook', x: 55, y: 120, r: 20, iconScale: 0.625, drift: 'a' },
  { icon: 'x', x: 340, y: 100, r: 20, iconScale: 0.625, drift: 'b' },
  { icon: 'news', x: 70, y: 260, r: 18, iconScale: 0.58, drift: 'c' },
  { icon: 'threads', x: 330, y: 280, r: 16, iconScale: 0.5, drift: 'a' },
  { icon: 'telegram', x: 60, y: 400, r: 16, iconScale: 0.5, drift: 'b' },
  { icon: 'rss', x: 340, y: 420, r: 14, iconScale: 0.45, drift: 'c' },
  { icon: 'linkedin', x: 130, y: 470, r: 14, iconScale: 0.45, drift: 'a' },
  { icon: 'monitor', x: 280, y: 480, r: 14, iconScale: 0.45, drift: 'b' },
]

const MOBILE_PATHS = M_NODES.map((n) => curvedPathToCenter(n.x, n.y, MCX, MCY, 0.12))

function MobileField({ id, pingCount }: { id: string; pingCount: number }) {
  const lineColor = getThemeColor('--chart-line')
  const glowColor = getThemeColor('--chart-glow')
  const nodeStroke = getThemeColor('--chart-node-stroke')
  const nodeFill = getThemeColor('--chart-node-fill')
  const iconColor = getThemeColor('--chart-label')
  const packetColor = getThemeColor('--chart-particle')

  return (
    <svg
      viewBox="0 0 400 540"
      className="absolute inset-0 h-full w-full"
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        <filter id={`${id}-mgl`} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="1.5" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <filter id={`${id}-mpk`} x="-100%" y="-100%" width="300%" height="300%">
          <feDropShadow
            dx="0"
            dy="0"
            stdDeviation="2"
            floodColor={packetColor}
            floodOpacity="0.5"
          />
        </filter>
      </defs>

      {[0, 3, 6, 9].map((d, i) => (
        <circle
          key={i}
          cx={MCX}
          cy={MCY}
          r="25"
          fill="none"
          stroke={lineColor}
          strokeWidth="0.5"
          className="intel-radar-expand"
          style={{ '--radar-expand-delay': `${d}s` } as React.CSSProperties}
        />
      ))}

      {M_NODES.map((node, i) => {
        const path = MOBILE_PATHS[i]
        const icon = ICONS[node.icon]
        const s = nodeSeed(i)
        const dur = (4.5 + s * 2).toFixed(1)
        const bd = (s * 5).toFixed(2)
        const iconOff = 12 * node.iconScale

        return (
          <g key={i}>
            <path
              d={path}
              fill="none"
              stroke={glowColor}
              strokeWidth="2"
              filter={`url(#${id}-mgl)`}
              className="intel-connection"
              style={
                { '--intel-delay': `${(i * 0.12).toFixed(2)}s` } as React.CSSProperties
              }
            />
            <path
              d={path}
              fill="none"
              stroke={lineColor}
              strokeWidth="0.6"
              strokeOpacity="0.10"
              className="intel-connection"
              style={
                { '--intel-delay': `${(i * 0.12).toFixed(2)}s` } as React.CSSProperties
              }
            />
            <path
              d={path}
              fill="none"
              stroke={lineColor}
              strokeWidth="0.8"
              strokeLinecap="round"
              pathLength={1}
              className="intel-scan-pulse"
              style={
                {
                  '--scan-dur': '2.5s',
                  '--scan-delay': `${(s * 3).toFixed(1)}s`,
                } as React.CSSProperties
              }
            />

            {[0, 0.4].map((off, pi) => (
              <circle key={pi} r={pi === 0 ? 2 : 1} fill={packetColor} opacity="0">
                <animateMotion
                  path={path}
                  dur={dur}
                  begin={`${(parseFloat(bd) + off).toFixed(2)}s`}
                  repeatCount="indefinite"
                  calcMode="spline"
                  keySplines="0.4 0 0.2 1"
                  keyTimes="0;1"
                />
                <animate
                  attributeName="opacity"
                  values="0;0.6;0.6;0"
                  keyTimes="0;0.1;0.75;1"
                  dur={dur}
                  begin={`${(parseFloat(bd) + off).toFixed(2)}s`}
                  repeatCount="indefinite"
                />
              </circle>
            ))}

            <g className={`intel-drift-${node.drift}`}>
              <g transform={`translate(${node.x},${node.y})`}>
                <circle
                  r={node.r}
                  fill={nodeFill}
                  stroke={nodeStroke}
                  strokeWidth="1"
                  className="intel-node"
                  style={
                    { '--intel-delay': `${(i * 0.1).toFixed(2)}s` } as React.CSSProperties
                  }
                />
                <g
                  transform={`translate(${-iconOff},${-iconOff}) scale(${node.iconScale})`}
                >
                  {icon.fill ? (
                    <path d={icon.d} fill={iconColor} />
                  ) : (
                    <path
                      d={icon.d}
                      fill="none"
                      stroke={iconColor}
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  )}
                </g>
              </g>
            </g>
          </g>
        )
      })}

      {pingCount > 0 && (
        <g key={`mping-${pingCount}`}>
          {[0, 0.15].map((d, i) => (
            <circle
              key={i}
              cx={MCX}
              cy={MCY}
              r="8"
              fill="none"
              stroke={lineColor}
              strokeWidth={i === 0 ? 1.2 : 0.6}
              className="intel-blink-ping"
              style={{ '--ping-delay': `${d}s` } as React.CSSProperties}
            />
          ))}
        </g>
      )}
    </svg>
  )
}

/* ── Main Component ── */

export function IntelligenceMap() {
  const id = useId()
  const { ref, isVisible } = useScrollReveal({ threshold: 0.05 })
  const [pingCount, setPingCount] = useState(0)

  const handleBlinkComplete = useCallback(() => {
    setPingCount((c) => c + 1)
  }, [])

  return (
    <section className="relative mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
      <div
        ref={ref}
        className={cn(
          'scroll-reveal relative min-h-[540px] sm:min-h-[620px] lg:min-h-[780px]',
          isVisible && 'is-visible',
        )}
      >
        {/* Desktop */}
        <div className="hidden lg:block lg:h-[780px]">
          <DesktopField id={id} pingCount={pingCount} />
        </div>

        {/* Mobile */}
        <div className="relative h-[540px] sm:h-[620px] lg:hidden">
          <MobileField id={`${id}-m`} pingCount={pingCount} />
        </div>

        {/* Side social stack */}
        <SourceDock />

        {/* Center: DB Shell encasing the blinking Eye */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="relative h-[150px] w-32 sm:h-[170px] sm:w-36 lg:h-[208px] lg:w-44">
            {/* DB shell — fine-line SVG behind the eye */}
            <DbShell className="absolute inset-0 h-full w-full" />
            {/* MasterEye — centered within the shell */}
            <div className="absolute inset-0 flex items-center justify-center">
              <MasterEye
                size={96}
                onBlinkComplete={handleBlinkComplete}
                className="h-[72px] w-[72px] sm:h-[88px] sm:w-[88px] lg:h-[104px] lg:w-[104px]"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
